import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { format, parseISO, differenceInHours } from "date-fns";
import { Trash, ArrowCounterClockwise, Warning, Clock } from "@phosphor-icons/react";

export default function TrashPage() {
  const [trashItems, setTrashItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrash();
    // Update countdown every minute
    const interval = setInterval(fetchTrash, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchTrash = async () => {
    try {
      const response = await api.get("/trash");
      setTrashItems(response.data);
    } catch (error) {
      toast.error("Erro ao carregar lixeira");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      await api.post(`/trash/${id}/restore`);
      toast.success("Conta restaurada com sucesso!");
      fetchTrash();
    } catch (error) {
      toast.error("Erro ao restaurar conta");
    }
  };

  const handleDeletePermanently = async (id) => {
    if (!window.confirm("Excluir permanentemente? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/trash/${id}`);
      toast.success("Excluído permanentemente");
      fetchTrash();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = parseISO(expiresAt);
    const hoursLeft = differenceInHours(expires, now);
    
    if (hoursLeft <= 0) return "Expirando...";
    if (hoursLeft === 1) return "1 hora restante";
    return `${hoursLeft} horas restantes`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trash-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Lixeira</h1>
          <p className="text-muted-foreground mt-1">
            Itens excluídos são removidos automaticamente após 24 horas
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="border-yellow-500/30 bg-yellow-500/5">
        <CardContent className="p-4 flex items-center gap-3">
          <Warning size={24} className="text-yellow-600" weight="fill" />
          <p className="text-sm text-yellow-800">
            Os itens na lixeira serão excluídos permanentemente após 24 horas. 
            Restaure-os antes se precisar recuperá-los.
          </p>
        </CardContent>
      </Card>

      {/* Trash Items */}
      {trashItems.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Trash size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">A lixeira está vazia</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {trashItems.map((item, idx) => {
            const hoursLeft = differenceInHours(parseISO(item.expires_at), new Date());
            const isUrgent = hoursLeft <= 6;

            return (
              <Card 
                key={item.id} 
                className={`stagger-item ${isUrgent ? 'border-destructive/50' : ''}`}
                data-testid={`trash-item-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                      <Trash size={24} className="text-muted-foreground" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-foreground">
                        {item.description}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-muted-foreground font-mono">
                          {formatCurrency(item.total_value)}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Excluído em {format(parseISO(item.deleted_at), "dd/MM/yyyy HH:mm")}
                        </span>
                      </div>
                    </div>

                    <Badge 
                      variant={isUrgent ? "destructive" : "secondary"}
                      className="gap-1.5"
                    >
                      <Clock size={14} />
                      {getTimeRemaining(item.expires_at)}
                    </Badge>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(item.id)}
                        className="gap-1.5"
                        data-testid={`restore-${item.id}`}
                      >
                        <ArrowCounterClockwise size={16} />
                        Restaurar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePermanently(item.id)}
                        className="gap-1.5 text-destructive hover:text-destructive"
                        data-testid={`delete-permanent-${item.id}`}
                      >
                        <Trash size={16} />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
